'use client';

import React, { useEffect, useRef, useState } from 'react';

interface StatProps {
  value: string;
  label: string;
  target: number;
  suffix: string;
  prefix?: string;
}

function AnimatedStat({ value, label, target, suffix, prefix = '' }: StatProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 2000;
          const step = target / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="stat-item text-center">
      <div className="text-4xl sm:text-5xl font-bold gradient-text mb-1 tabular-nums">
        {prefix}{count}{suffix}
      </div>
      <div className="text-sm text-neutral-500 font-medium">{label}</div>
    </div>
  );
}

const partners = [
  { name: 'Vertex', icon: 'triangle' },
  { name: 'Omni Labs', icon: 'circle' },
  { name: 'Quantum', icon: 'zap' },
  { name: 'Nexus AI', icon: 'hexagon' },
  { name: 'Apex Scale', icon: 'bar-chart' },
  { name: 'GlobalFlow', icon: 'globe' },
];

function PartnerIcon({ type }: { type: string }) {
  const props = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (type) {
    case 'triangle':
      return <svg {...props}><path d="M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /></svg>;
    case 'circle':
      return <svg {...props}><circle cx="12" cy="12" r="10" /></svg>;
    case 'zap':
      return <svg {...props}><path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" /></svg>;
    case 'hexagon':
      return <svg {...props}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>;
    case 'bar-chart':
      return <svg {...props}><line x1="18" x2="18" y1="20" y2="10" /><line x1="12" x2="12" y1="20" y2="4" /><line x1="6" x2="6" y1="20" y2="14" /></svg>;
    case 'globe':
      return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></svg>;
    default:
      return null;
  }
}

export default function SocialProofSection() {
  const allPartners = [...partners, ...partners];

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(rgb(5, 5, 5), rgb(8, 8, 16), rgb(5, 5, 5))' }}
    >
      {/* Top line */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16"
        style={{ background: 'linear-gradient(transparent, rgba(59, 130, 246, 0.5))' }}
      />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400 mb-4 block">Proven Impact</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Numbers That Speak</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-20">
          <div className="reveal reveal-delay-1 glass-card rounded-2xl p-6 text-center">
            <AnimatedStat value="500M+" label="Views Generated" target={500} suffix="M+" />
          </div>
          <div className="reveal reveal-delay-2 glass-card rounded-2xl p-6 text-center">
            <AnimatedStat value="100+" label="Clients Helped" target={100} suffix="+" />
          </div>
          <div className="reveal reveal-delay-3 glass-card rounded-2xl p-6 text-center">
            <AnimatedStat value="2B+" label="Organic Reach" target={2} suffix="B+" />
          </div>
          <div className="reveal reveal-delay-4 glass-card rounded-2xl p-6 text-center">
            <AnimatedStat value="250+" label="AI Workflows Built" target={250} suffix="+" />
          </div>
        </div>

        {/* Trusted By */}
        <div className="reveal text-center mb-8">
          <span className="text-xs text-neutral-600 tracking-[0.2em] uppercase font-medium">Trusted by brands worldwide</span>
        </div>

        {/* Marquee */}
        <div
          className="relative overflow-hidden"
          style={{ maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)' }}
        >
          <div className="flex gap-0 marquee-track" style={{ width: 'max-content' }}>
            {allPartners.map((partner, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 mx-8 py-3"
                style={{ minWidth: 'max-content' }}
              >
                <span className="text-blue-500/60">
                  <PartnerIcon type={partner.icon} />
                </span>
                <span className="text-neutral-500 font-semibold text-sm tracking-wide">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
